import { DirectUpload as DirectUploadController } from 'activestorage'
import classNames from 'classnames'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Button } from 'semantic-ui-react'
import { FormInputError } from 'shared'
import styles from './direct_upload.module.css'
import { DirectUploadProps } from './types'
const UPLOAD_URL = '/rails/active_storage/direct_uploads'
const MAX_FILE_SIZE_MB = 10
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

const DirectUpload = ({
  content = 'Hello',
  error,
  onUpload,
  onError,
  triggerComponent: Trigger = Button,
  className,
  loading: loading_, // TODO: Pass mutation loading flag
}: DirectUploadProps) => {
  const [busy, setBusy] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    async (event) => {
      const files = Array.from(event?.target?.files ?? [])
      if (files.length === 0) return

      onError?.(null)

      const heicFiles = files.filter(
        (file) =>
          file.type === 'image/heic' ||
          file.type === 'image/heif' ||
          file.name.toLowerCase().endsWith('.heic') ||
          file.name.toLowerCase().endsWith('.heif')
      )
      const nonHeicFiles = files.filter((f) => !heicFiles.includes(f))

      if (heicFiles.length > 0) {
        onError?.('HEIC/HEIF images are not supported. Please convert to JPEG or PNG before uploading.')
      }

      const validFiles = nonHeicFiles.filter((file) => file.size <= MAX_FILE_SIZE_BYTES)

      if (validFiles.length < nonHeicFiles.length) {
        onError?.(`File size must be less than ${MAX_FILE_SIZE_MB}MB`)
      }

      event.target.value = ''

      if (validFiles.length === 0) return

      setBusy(true)

      for (const file of validFiles) {
        await new Promise<void>((resolve) => {
          const controller = new DirectUploadController(file, UPLOAD_URL)
          controller.create((err, attributes) => {
            if (!err) onUpload(attributes.signed_id, URL.createObjectURL(file))
            resolve()
          })
        })
      }

      setBusy(false)
    },
    [onUpload]
  )

  const loading = useMemo(() => loading_ || busy, [loading_, busy])
  const handleTriggerClick: React.MouseEventHandler = useCallback((event) => {
    event.preventDefault()
    fileInputRef.current && fileInputRef.current.click()
  }, [])

  return (
    <div
      className={classNames(styles.buttonContainer, className, 'flex flex-col', {
        ['error']: !!error,
      })}
    >
      {error && (
        <div>
          <FormInputError errorSymbol={error} />
        </div>
      )}
      <div className={classNames('ui', 'input', styles.input, className)}>
        <input
          type="file"
          multiple
          accept="image/jpeg,image/png,.jpg,.jpeg,.png"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          ref={fileInputRef}
        />
        <Trigger onClick={handleTriggerClick} content={content} loading={loading} primary />
      </div>
    </div>
  )
}

export default DirectUpload
